import { useMemo } from "react";
import { queryOptions, useQueries } from "@tanstack/react-query";
import { getWorkerTimeline } from "../api/workers";
import type { TimelineEvent } from "../api/types";
import { localDayBounds, localDayKey } from "../lib/date";
import { groupByLocalDay } from "../lib/dayRecap";

// Le détail brut d'une période était chargé en UN appel timeline sur toute la
// période avec limit=500. La sur-émission des events d'activité (200+/jour)
// dépasse ce plafond dès qu'une période fait plus de ~2 jours : le back trie
// `occurred_at DESC LIMIT 500`, donc seuls les jours les plus récents
// reviennent et les premiers jours s'affichent vides. On charge désormais un
// jour par requête (max mesuré ~417 events/jour, très en deçà du plafond), ce
// qui borne chaque appel à une seule journée et supprime la troncature.
const PER_DAY_LIMIT = 1000;

// Énumère les jours locaux inclus dans [startKey, endKey] (bornes YYYY-MM-DD).
function enumerateDays(startKey: string, endKey: string): string[] {
  const [sy, sm, sd] = startKey.split("-").map(Number);
  const [ey, em, ed] = endKey.split("-").map(Number);
  if (!sy || !sm || !sd || !ey || !em || !ed) return [];
  const cur = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const days: string[] = [];
  while (cur <= end) {
    days.push(localDayKey(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

// Un jour passé ne bouge presque plus : le relire toutes les 30 s ne sert à
// rien (le récap d'une équipe, c'est une requête par membre et par jour).
const TODAY_STALE_MS = 30_000;
const PAST_DAY_STALE_MS = 5 * 60_000;

/** Timeline d'un worker sur un jour LOCAL. Clé partagée par la feuille de temps,
 * la carte « Hier », le récap équipe et la fraîcheur des données. */
export function workerTimelineDayQuery(
  workerUrl: string,
  day: string,
  enabled = true,
) {
  const { from, to } = localDayBounds(day);
  return queryOptions({
    queryKey: ["tawa", "worker", "timeline", "day", workerUrl, day],
    queryFn: () =>
      getWorkerTimeline({
        worker_url: workerUrl,
        from,
        to,
        limit: PER_DAY_LIMIT,
      }),
    enabled: enabled && !!workerUrl,
    staleTime:
      day < localDayKey(new Date()) ? PAST_DAY_STALE_MS : TODAY_STALE_MS,
  });
}

/**
 * Charge le détail brut de la timeline d'un worker sur une période, un jour par
 * requête, et rend les events regroupés par jour local. Remplace un fetch
 * unique sur toute la période qui tronquait les premiers jours (limit=500).
 */
export function useWorkerTimelineDays(
  workerUrl: string,
  startKey: string,
  endKey: string,
  options?: { enabled?: boolean },
): {
  eventsByDay: Map<string, TimelineEvent[]>;
  isLoading: boolean;
  isError: boolean;
} {
  const days = useMemo(
    () => enumerateDays(startKey, endKey),
    [startKey, endKey],
  );
  const enabled = options?.enabled ?? true;

  const results = useQueries({
    queries: days.map((day) => workerTimelineDayQuery(workerUrl, day, enabled)),
  });

  const updatedSignal = results.map((r) => r.dataUpdatedAt).join(",");
  const eventsByDay = useMemo(
    () => groupByLocalDay(results.flatMap((r) => r.data?.events ?? [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updatedSignal],
  );

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);
  return { eventsByDay, isLoading, isError };
}
